import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { Search } from 'lucide-react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.vfs;

const Transactions = ({
  component,
  setComponent,
  transactions,
  setTransactions,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [downloadOption, setDownloadOption] = useState('');

  // Replace your handleCSVDownload function with this:
  const handleCSVDownload = () => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      alert('No transaction data to export.');
      return;
    }

    const headers = Object.keys(filteredTransactions[0]); // Get column names

    const csvRows = [
      headers.join(','), // Header row
      ...filteredTransactions.map((transaction) =>
        headers
          .map((field) => {
            const value = transaction[field] ?? ''; // Fallback to empty if undefined/null
            return `"${String(value).replace(/"/g, '""')}"`; // Escape quotes
          })
          .join(',')
      ),
    ];

    const csv = csvRows.join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'filtered_transactions.csv';
    a.click();

    URL.revokeObjectURL(url); // Clean up
  };

  // Replace your downloadTransactionsAsPDF function with this:
  const downloadTransactionsAsPDF = (transactionsData) => {
    if (!transactionsData || transactionsData.length === 0) {
      alert('No transaction data to export.');
      return;
    }

    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [25, 35, 25, 40],

      header: {
        margin: [25, 15, 25, 0],
        table: {
          widths: ['*', 'auto'],
          body: [
            [
              {
                stack: [
                  { text: 'AcademiaHub', style: 'headerCompany' },
                  {
                    text: 'Educational Management System',
                    style: 'headerSubtitle',
                  },
                ],
                margin: [0, 5, 0, 0],
              },
              {
                stack: [
                  { text: 'TRANSACTION REPORT', style: 'headerTitle' },
                  {
                    text: `Generated: ${new Date().toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}`,
                    style: 'headerDate',
                  },
                ],
                alignment: 'right',
                margin: [0, 5, 0, 0],
              },
            ],
          ],
        },
        layout: 'noBorders',
      },

      content: [
        {
          margin: [0, 20, 0, 15],
          table: {
            widths: ['*', '*', '*', '*'],
            body: [
              [
                { text: 'Total Transactions', style: 'summaryLabel' },
                { text: 'Total Amount', style: 'summaryLabel' },
                { text: 'Unique Payees', style: 'summaryLabel' },
                { text: 'Unique Receivers', style: 'summaryLabel' },
              ],
              [
                {
                  text: transactionsData.length.toString(),
                  style: 'summaryValue',
                },
                {
                  text: `$${transactionsData
                    .reduce((acc, tx) => acc + (tx.amount || 0), 0)
                    .toFixed(2)}`,
                  style: 'summaryValue',
                },
                {
                  text: new Set(
                    transactionsData.map((tx) => tx.payee)
                  ).size.toString(),
                  style: 'summaryValue',
                },
                {
                  text: new Set(
                    transactionsData.map((tx) => tx.receiver)
                  ).size.toString(),
                  style: 'summaryValue',
                },
              ],
            ],
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? '#34495e' : '#ecf0f1'),
            textColor: (rowIndex) => (rowIndex === 0 ? 'white' : '#2c3e50'),
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 6,
            paddingBottom: () => 6,
          },
        },

        {
          table: {
            headerRows: 1,
            widths: [30, 70, 60, 110, 100, 100, '*', 60],
            body: [
              [
                { text: '#', style: 'tableHeader' },
                { text: 'Date', style: 'tableHeader' },
                { text: 'Time', style: 'tableHeader' },
                { text: 'Transaction ID', style: 'tableHeader' },
                { text: 'Payee', style: 'tableHeader' },
                { text: 'Receiver', style: 'tableHeader' },
                { text: 'Course', style: 'tableHeader' },
                { text: 'Amount', style: 'tableHeader' },
              ],
              ...transactionsData.map((tx, index) => {
                const dateObj = new Date(tx.date);
                const date = dateObj.toLocaleDateString('en-US', {
                  dateStyle: 'medium',
                });
                const time = dateObj.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                });

                return [
                  {
                    text: String(index + 1).padStart(3, '0'),
                    style: 'cellData',
                    alignment: 'center',
                    fillColor: '#f8f9fa',
                  },
                  {
                    text: date || 'N/A',
                    style: 'dateText',
                    alignment: 'center',
                  },
                  {
                    text: time || 'N/A',
                    style: 'timeText',
                    alignment: 'center',
                  },
                  { text: tx._id || 'N/A', style: 'cellData' },
                  {
                    stack: [
                      { text: tx.payee || 'N/A', style: 'cellData' },
                      { text: tx.payeeUsername || '', style: 'timeText' },
                      { text: tx.payeeEmail || '', style: 'teacherEmail' },
                    ],
                  },
                  {
                    stack: [
                      { text: tx.receiver || 'N/A', style: 'cellData' },
                      { text: tx.receiverUsername || '', style: 'timeText' },
                      { text: tx.receiverEmail || '', style: 'teacherEmail' },
                    ],
                  },
                  { text: tx.courseName || 'N/A', style: 'cellData' },
                  {
                    text: `$${(tx.amount || 0).toFixed(2)}`,
                    style: 'statsText',
                    alignment: 'right',
                  },
                ];
              }),
            ],
          },
          layout: {
            fillColor: (rowIndex) =>
              rowIndex === 0
                ? '#2c3e50'
                : rowIndex % 2 === 0
                ? '#fdfdfd'
                : null,
            textColor: (rowIndex) => (rowIndex === 0 ? 'white' : '#2c3e50'),
            hLineColor: '#bdc3c7',
            vLineColor: '#bdc3c7',
            hLineWidth: (i, node) =>
              i === 0 || i === node.table.body.length ? 1 : 0.5,
            vLineWidth: () => 0.5,
            paddingLeft: () => 4,
            paddingRight: () => 4,
            paddingTop: () => 4,
            paddingBottom: () => 4,
          },
        },
      ],

      footer: (currentPage, pageCount) => ({
        margin: [25, 10, 25, 0],
        table: {
          widths: ['*', 'auto', '*'],
          body: [
            [
              {
                text: 'AcademiaHub Educational Management System',
                style: 'footerLeft',
              },
              {
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerCenter',
              },
              { text: '© 2025 - Confidential Document', style: 'footerRight' },
            ],
          ],
        },
        layout: 'noBorders',
      }),

      styles: {
        headerCompany: { fontSize: 18, bold: true, color: '#2c3e50' },
        headerSubtitle: { fontSize: 9, color: '#7f8c8d', italics: true },
        headerTitle: { fontSize: 16, bold: true, color: '#2c3e50' },
        headerDate: { fontSize: 8, color: '#7f8c8d' },

        summaryLabel: { fontSize: 9, bold: true, alignment: 'center' },
        summaryValue: { fontSize: 14, bold: true, alignment: 'center' },

        tableHeader: {
          fontSize: 9,
          bold: true,
          alignment: 'center',
          color: 'white',
        },
        cellData: { fontSize: 8, color: '#2c3e50' },
        dateText: { fontSize: 8, color: '#2c3e50', bold: true },
        timeText: { fontSize: 7, color: '#95a5a6' },
        teacherEmail: { fontSize: 7, color: '#7f8c8d', italics: true },
        statsText: { fontSize: 8, bold: true, color: '#27ae60' },

        footerLeft: { fontSize: 8, color: '#7f8c8d', alignment: 'left' },
        footerCenter: {
          fontSize: 8,
          color: '#2c3e50',
          alignment: 'center',
          bold: true,
        },
        footerRight: { fontSize: 8, color: '#7f8c8d', alignment: 'right' },
      },
    };

    pdfMake
      .createPdf(docDefinition)
      .download('AcademiaHub_Filtered_Transaction_Report.pdf');
  };

  // Update your handleDownloadChange function:
  const handleDownloadChange = (e) => {
    const value = e.target.value;
    setDownloadOption(''); // Reset dropdown after selection

    if (value === 'csv') {
      handleCSVDownload(); // This now uses filteredTransactions
    } else if (value === 'pdf') {
      downloadTransactionsAsPDF(filteredTransactions); // Pass filteredTransactions here
    }
  };
  const getFilteredDate = (filter) => {
    const now = new Date();
    switch (filter) {
      case 'lastWeek':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case 'lastMonth':
        const lastMonth = new Date(now);
        lastMonth.setMonth(now.getMonth() - 1);
        return lastMonth;
      case 'last3Months':
        const last3Months = new Date(now);
        last3Months.setMonth(now.getMonth() - 3);
        return last3Months;
      case 'last6Months':
        const last6Months = new Date(now);
        last6Months.setMonth(now.getMonth() - 6);
        return last6Months;
      default:
        return null;
    }
  };

  const filteredTransactions = transactions.filter((transaction) => {
    // Search filter - by payee name, receiver name, or transaction ID
    const matchesSearch =
      transaction.payee?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.receiver?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction._id?.toLowerCase().includes(searchQuery.toLowerCase());

    // If no date filter is applied, return search matches
    if (!dateFilter) {
      return matchesSearch;
    }

    // Apply date filter if provided - filter by transaction date
    const filterDate = getFilteredDate(dateFilter);
    if (!filterDate) {
      return matchesSearch;
    }

    const transactionDate = new Date(transaction.date);

    // Handle invalid dates
    if (isNaN(transactionDate.getTime())) {
      return matchesSearch;
    }

    return matchesSearch && transactionDate >= filterDate;
  });

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
  <h2 className="text-2xl font-semibold flex items-center">
    <button
      className="p-2 hover:bg-gray-100 rounded mr-2"
      onClick={() => setComponent('AdminDashboard')}
    >
      <ArrowLeft className="h-6 w-6 text-gray-500" />
    </button>
    Transaction History
  </h2>

  <div className="w-full md:w-auto">
    <select
      onChange={handleDownloadChange}
      value={downloadOption}
      className="w-full md:w-auto bg-indigo-500 text-white font-bold py-2 px-4 rounded cursor-pointer"
    >
      <option value="" disabled className="text-black bg-white border-bottom">
        Select download format
      </option>
      <option value="csv" className="text-black bg-white">
        Download as CSV
      </option>
      <option value="pdf" className="text-black bg-white">
        Download as PDF
      </option>
    </select>
  </div>
</div>

      {/* Your existing Search and Filters section - keep as is */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex w-full items-center justify-between gap-4">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by payee, receiver, or transaction ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Time</option>
            <option value="lastWeek">Last Week</option>
            <option value="lastMonth">Last Month</option>
            <option value="last3Months">Last 3 Months</option>
            <option value="last6Months">Last 6 Months</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto mt-10">
        <table className="min-w-full bg-white rounded shadow">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="py-3 px-4">#</th>
              <th className="py-2 px-4 text-left">Date</th>
              <th className="py-2 px-4 text-left">Time</th>
              <th className="py-3 px-4">Transaction ID</th>
              <th className="py-3 px-4">Payee (Student)</th>
              <th className="py-3 px-4">Receiver (Teacher)</th>
              <th className="py-3 px-4">Course</th>
              <th className="py-3 px-4">Amount</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-500">
                  {searchQuery || dateFilter
                    ? 'No transactions found matching your criteria.'
                    : 'No transactions found.'}
                </td>
              </tr>
            ) : (
              filteredTransactions.map((tx, index) => {
                const dateObj = new Date(tx.date);
                const date = dateObj.toLocaleDateString('en-US', {
                  dateStyle: 'medium',
                });
                const time = dateObj.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                });
                return (
                  <tr key={tx._id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium">{index + 1}</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{date}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{time}</td>
                    <td className="py-3 px-4">{tx._id}</td>
                    <td className="py-3 px-4">
                      <div className="font-medium">{tx.payee}</div>
                      <div className="text-sm text-gray-500">
                        {tx.payeeUsername}
                      </div>
                      <div className="text-sm text-gray-400">
                        {tx.payeeEmail}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium">{tx.receiver}</div>
                      <div className="text-sm text-gray-500">
                        {tx.receiverUsername}
                      </div>
                      <div className="text-sm text-gray-400">
                        {tx.receiverEmail}
                      </div>
                    </td>
                    <td className="py-3 px-4">{tx.courseName}</td>
                    <td className="py-3 px-4 text-green-600 font-semibold">
                      ${tx.amount}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Transactions;
